/**
 * What: Versioned transport schemas for merchant-safe Shopify credit balance and activity log.
 * Transport / data-flow: Shared between apps/web signed routes and apps/shopify UI hooks.
 */
import * as z from 'zod';
export const SHOPIFY_CREDIT_BALANCE_SCHEMA_VERSION = 'shopify-credit-balance.v1';
// Explicit rollout capability: older Shopify bundles require numeric balances.
export const SHOPIFY_CREDIT_RECEIPTS_CAPABILITY = 'credit-receipts';
export const SHOPIFY_CREDIT_ACTIVITY_SCHEMA_VERSION = 'shopify-credit-activity.v1';
export const shopifyCreditBalanceStateSchema = z.enum(['no_plan', 'configured']);
export const shopifyCreditBalanceNoPlanSchema = z.object({
    schemaVersion: z.literal(SHOPIFY_CREDIT_BALANCE_SCHEMA_VERSION),
    state: z.literal('no_plan'),
    canSpend: z.literal(false),
    reason: z.literal('no_plan'),
});
export const shopifyCreditBalanceConfiguredSchema = z.object({
    schemaVersion: z.literal(SHOPIFY_CREDIT_BALANCE_SCHEMA_VERSION),
    state: z.literal('configured'),
    plan: z.object({
        kind: z.enum(['shopify', 'internal']),
        name: z.string(),
        code: z.string(),
    }),
    includedCredits: z.number().int().nonnegative(),
    allowanceOverride: z
        .object({
        totalCredits: z.number().int().nonnegative(),
        expiresAt: z.string().datetime({ offset: true }),
    })
        .optional(),
    grantedCredits: z.number().int().nonnegative(),
    committedCredits: z.number().int().nonnegative(),
    reservedCredits: z.number().int().nonnegative(),
    availableCredits: z.number().int().nonnegative(),
    resetsAt: z.string().datetime({ offset: true }),
    canSpend: z.boolean(),
});
export const shopifyCreditBalanceSchema = z.discriminatedUnion('state', [
    shopifyCreditBalanceNoPlanSchema,
    shopifyCreditBalanceConfiguredSchema,
]);
/** This month's whole allowance: a temporary override replaces plan + granted credits. */
export function getShopifyCreditAllowance(balance) {
    return balance.allowanceOverride?.totalCredits ?? balance.includedCredits + balance.grantedCredits;
}
export const shopifyCreditActivityKindSchema = z.enum(['allowance', 'grant', 'pending', 'charge', 'refund']);
export const shopifyCreditActivityStatusSchema = z.enum(['effective', 'pending']);
export const shopifyCreditActivityItemSchema = z.object({
    id: z.string(),
    occurredAt: z.string(),
    kind: shopifyCreditActivityKindSchema,
    operationKey: z.string().nullable(),
    creditDelta: z.number().int(),
    balanceAfter: z.number().int().nullable(),
    status: shopifyCreditActivityStatusSchema,
    description: z.string().optional(),
    studyReference: z.string().optional(),
    studyTitle: z.string().optional(),
    reason: z.enum(['study_failed']).optional(),
    originalChargeId: z.string().optional(),
});
export const shopifyCreditActivityPageSchema = z.object({
    schemaVersion: z.literal(SHOPIFY_CREDIT_ACTIVITY_SCHEMA_VERSION),
    items: z.array(shopifyCreditActivityItemSchema),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=shopify-credit-activity-v1.js.map